/**
 * Zustand store for theme and appearance configuration.
 *
 * State:
 *   - colors: { primary, secondary, surface, accent } — hex color strings
 *   - fonts:  { heading, body } — CSS font-family names
 *   - logoUrl: string — URL of the org's uploaded logo
 *   - chatBubbleStyle: 'rounded' | 'flat' | 'sharp' | 'floating'
 *   - isDark: boolean — dark mode toggle
 *   - language: 'en' | 'fr' | 'ar'
 *   - rtlLanguages: string[] — languages that use RTL layout (always contains 'ar')
 *   - isDirty: boolean — true when there are unsaved local changes
 *   - isLoading: boolean
 *
 * Actions:
 *   - loadAppearance(orgId)      — GET /api/v1/appearance → hydrates store
 *   - saveAppearance(orgId)      — PUT /api/v1/appearance → persists current state
 *   - updateColor(key, value)    — update a single color, marks isDirty = true
 *   - updateFont(key, value)     — update a single font, marks isDirty = true
 *   - setLogoUrl(url)            — update logo URL after upload
 *   - toggleDarkMode()           — flip isDark, re-inject CSS vars
 *   - setLanguage(lang)          — change language, update document.dir for RTL
 *   - injectThemeVars()          — write CSS custom properties to :root
 *   - resetToDraft()             — discard unsaved changes by re-fetching from API
 *
 * Persistence:
 *   - Persists theme preferences to localStorage key "helix_theme"
 *   - localStorage is only for user preference (isDark, language)
 *   - org-specific appearance (colors, fonts, logo) always loads from API
 *
 * CSS injection:
 *   - injectThemeVars() is called after every state mutation that affects visuals
 *   - Sets: --color-primary, --color-secondary, --color-surface, --color-accent,
 *            --font-heading, --font-body on :root
 *   - Dark mode sets a `data-theme="dark"` attribute on <html>
 *
 * Sync:
 *   - Local preview changes (updateColor, updateFont) do NOT auto-save
 *   - Call saveAppearance(orgId) explicitly to persist to backend
 *   - isDirty flag lets the UI show "Unsaved changes" indicator
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChatBubbleStyle = 'rounded' | 'flat' | 'sharp' | 'floating';
export type Language = 'en' | 'fr' | 'ar';

export interface ThemeColors {
  primary: string;
  secondary: string;
  surface: string;
  accent: string;
  text: string;
  textMuted: string;
}

export interface ThemeFonts {
  heading: string;
  body: string;
}

export interface AppearanceConfig {
  colors: ThemeColors;
  fonts: ThemeFonts;
  logoUrl: string;
  chatBubbleStyle: ChatBubbleStyle;
  welcomeMessage: string;
  language: Language;
}

export interface ThemeState extends AppearanceConfig {
  isDark: boolean;
  rtlLanguages: Language[];
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface ThemeActions {
  loadAppearance: (orgId: string) => Promise<void>;
  saveAppearance: (orgId: string) => Promise<void>;
  updateColor: (key: keyof ThemeColors, value: string) => void;
  updateFont: (key: keyof ThemeFonts, value: string) => void;
  setLogoUrl: (url: string) => void;
  setChatBubbleStyle: (style: ChatBubbleStyle) => void;
  toggleDarkMode: () => void;
  setTheme: (theme: 'dark' | 'light') => void;
  toggleSidebar: () => void;
  setLanguage: (lang: Language) => void;
  injectThemeVars: () => void;
  resetToDraft: (orgId: string) => Promise<void>;
  clearError: () => void;
}

export interface ThemeExtras {
  theme: 'dark' | 'light';
  sidebarCollapsed: boolean;
}

export type ThemeStore = ThemeState & ThemeActions & ThemeExtras;

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_COLORS: ThemeColors = {
  primary: '#8B5CF6',    // Helix brand violet
  secondary: '#6D28D9',
  surface: '#1E1B2E',
  accent: '#A78BFA',
  text: '#F9FAFB',
  textMuted: '#9CA3AF',
};

const DEFAULT_FONTS: ThemeFonts = {
  heading: 'Geist',
  body: 'Inter',
};

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const API_BASE = '/api/v1';

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(body.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// CSS injection helper (module-level, so it can be called outside the store)
// ---------------------------------------------------------------------------

function injectCSSVars(state: Pick<ThemeState, 'colors' | 'fonts' | 'isDark'>): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', state.colors.primary);
  root.style.setProperty('--color-secondary', state.colors.secondary);
  root.style.setProperty('--color-surface', state.colors.surface);
  root.style.setProperty('--color-accent', state.colors.accent);
  root.style.setProperty('--color-text', state.colors.text);
  root.style.setProperty('--color-text-muted', state.colors.textMuted);
  root.style.setProperty('--font-heading', state.fonts.heading);
  root.style.setProperty('--font-body', state.fonts.body);
  root.dataset['theme'] = state.isDark ? 'dark' : 'light';
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      // ── State ──────────────────────────────────────────────────────────────
      colors: DEFAULT_COLORS,
      fonts: DEFAULT_FONTS,
      logoUrl: '',
      chatBubbleStyle: 'rounded',
      welcomeMessage: 'Hello! How can I help you today?',
      language: 'en',
      isDark: true,
      theme: 'dark' as 'dark' | 'light',
      sidebarCollapsed: false,
      rtlLanguages: ['ar'],
      isDirty: false,
      isLoading: false,
      error: null,

      // ── Actions ────────────────────────────────────────────────────────────

      loadAppearance: async (orgId: string) => {
        set({ isLoading: true, error: null });
        try {
          const data = await apiFetch<AppearanceConfig>(`/appearance?org_id=${orgId}`);
          set({
            colors: data.colors,
            fonts: data.fonts,
            logoUrl: data.logoUrl,
            chatBubbleStyle: data.chatBubbleStyle,
            welcomeMessage: data.welcomeMessage,
            language: data.language,
            isDirty: false,
            isLoading: false,
          });
          injectCSSVars(get());
          get().setLanguage(data.language); // handle RTL
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to load appearance',
            isLoading: false,
          });
        }
      },

      saveAppearance: async (orgId: string) => {
        set({ isLoading: true, error: null });
        const state = get();
        try {
          await apiFetch<void>(`/appearance?org_id=${orgId}`, {
            method: 'PUT',
            body: JSON.stringify({
              colors: state.colors,
              fonts: state.fonts,
              logoUrl: state.logoUrl,
              chatBubbleStyle: state.chatBubbleStyle,
              welcomeMessage: state.welcomeMessage,
              language: state.language,
            }),
          });
          set({ isDirty: false, isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to save appearance',
            isLoading: false,
          });
          throw err;
        }
      },

      updateColor: (key, value) => {
        set((state) => ({
          colors: { ...state.colors, [key]: value },
          isDirty: true,
        }));
        injectCSSVars(get());
      },

      updateFont: (key, value) => {
        set((state) => ({
          fonts: { ...state.fonts, [key]: value },
          isDirty: true,
        }));
        injectCSSVars(get());
      },

      setLogoUrl: (url) => set({ logoUrl: url, isDirty: true }),

      setChatBubbleStyle: (style) => set({ chatBubbleStyle: style, isDirty: true }),

      toggleDarkMode: () => {
        set((state) => ({ isDark: !state.isDark, theme: state.isDark ? 'light' : 'dark' }));
        injectCSSVars(get());
      },

      setTheme: (theme) => {
        set({ theme, isDark: theme === 'dark' });
        injectCSSVars(get());
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setLanguage: (lang) => {
        set({ language: lang });
        const isRTL = get().rtlLanguages.includes(lang);
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
        localStorage.setItem('helix_lang', lang);
      },

      injectThemeVars: () => injectCSSVars(get()),

      resetToDraft: async (orgId) => {
        await get().loadAppearance(orgId);
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'helix_theme',
      // Only persist user preferences (dark mode, language) — not org-specific colors
      partialize: (state) => ({
        isDark: state.isDark,
        language: state.language,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          injectCSSVars(state);
          if (state.language) {
            const isRTL = state.rtlLanguages?.includes(state.language);
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.documentElement.lang = state.language;
          }
        }
      },
    },
  ),
);
